import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { OktaAuthStateService } from '@okta/okta-angular';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ISelectedContract, LoggedInUserSettings } from 'src/app/core/services/settings/logged-in-user-settings';
import { UserClaims } from 'src/app/model/okta/userClaims';
import { IChatSettings } from 'src/app/model/chat/IChatSettings';
import { CHAT, CHATGROUP } from '../../constants/feature-key.constants';
import { PGHOktaService } from '../../services/pghokta.service';
import { PreInitSettingsService, IPreInitAppSettings } from '../../services/settings/pre-init-setting.service';
import { ADPResourceService } from 'digital-core';

declare const embedded_svc;

@Component({
  selector: 'app-chat-agent',
  template: '',
  encapsulation: ViewEncapsulation.None
})
export class ChatAgentComponent implements OnInit, OnDestroy {
  chatOffHoursStartTimeInUTC = '4';
  chatOffHoursEndTimeInUTC = '12';
  isAuthenticated: boolean;
  private phoneNumber: string = '';
  private destroy$ = new Subject<boolean>();
  private chatConfig: IChatSettings;
  private isSPOEEnabled: boolean;

  constructor(
    public oktaAuth: OktaAuthStateService,
    private preInitSettingsService: PreInitSettingsService,
    private loggedInUserSettings: LoggedInUserSettings,
    private pghOktaService: PGHOktaService,
    public resourceService: ADPResourceService
  ) {
    this.oktaAuth.authState$.subscribe((authState) => (this.isAuthenticated = authState.isAuthenticated));
  }

  ngOnDestroy(): void {
    this.setChatButtonDisplay('none');
    this.destroy$.next(true);
  }

  async ngOnInit() {
    this.isAuthenticated = this.pghOktaService.isTokenValid();
    combineLatest([
      this.loggedInUserSettings.$userClaims,
      this.loggedInUserSettings.$selectedContract,
      this.preInitSettingsService.settings,
    ]).pipe(
      filter(([userClaims, selectedContract]) => !!userClaims && !!selectedContract),
      takeUntil(this.destroy$)
    ).subscribe(([userClaims, selectedContract, preInitSettings]: [UserClaims, ISelectedContract, IPreInitAppSettings]) => {
      this.isSPOEEnabled = this.resourceService.isPortalSettingTrue('Global_SPOE_Enabled');
      if (this.isSPOEEnabled) {
        this.hideChatButton();
      }
      this.chatOffHoursStartTimeInUTC = this.resourceService.getPortalSetting('Chat_OffHours_StartTime_UTC24HT')?.Value ?? '4';
      this.chatOffHoursEndTimeInUTC = this.resourceService.getPortalSetting('Chat_OffHours_EndTime_UTC24HT')?.Value ?? '12';
      const cancelledOrExpiredHasFullAccess = this.resourceService.isPortalSettingTrue('Global_CancelledOrExpiredContract_HasFullAccess');
      const now = new Date();
      if ((selectedContract.isCancelled || selectedContract.isExpired) && !cancelledOrExpiredHasFullAccess) {
        this.setChatButtonDisplay('none');
      } else if (now.getUTCHours() >= parseInt(this.chatOffHoursStartTimeInUTC) && now.getUTCHours() < parseInt(this.chatOffHoursEndTimeInUTC)) {
        this.setChatButtonDisplay('none');
      } else {
        this.setChatButtonDisplay('block');

        const member = selectedContract?.members?.find(m => m?.emailAddress?.toLowerCase() === userClaims?.email?.toLowerCase());
        if (!member)
          return;

        this.phoneNumber = member.phoneNumber;
        this.chatConfig = {
          JsFile: this.resourceService.getPortalSetting("Chat_JsFile")?.Value,
          PersonRecordTypeId: this.resourceService.getPortalSetting("Chat_PersonRecordTypeId")?.Value,
          isOfflineSupportEnabled: this.resourceService.isPortalSettingTrue("Chat_IsOfflineSupportEnabled"),
          EswLiveAgentDevName: this.resourceService.getPortalSetting("Chat_EswLiveAgentDevName")?.Value,
          CustomerLanguage: this.resourceService.getPortalSetting("Chat_CustomerLanguage")?.Value,
          BaseLiveAgentURL: this.resourceService.getPortalSetting("Chat_BaseLiveAgentURL")?.Value,
          ClientName: this.resourceService.getPortalSetting("Chat_ClientName")?.Value,
          PtpUrl: this.resourceService.getPortalSetting("Chat_PtpUrl")?.Value,
          ServiceUrl: this.resourceService.getPortalSetting("Chat_ServiceUrl")?.Value,
          ButtonId: (now.getUTCHours() >= parseInt(this.chatOffHoursStartTimeInUTC) && now.getUTCHours() < parseInt(this.chatOffHoursEndTimeInUTC)) ? '' : this.resourceService.getPortalSetting("Chat_ButtonId")?.Value,
          ChatCode: this.resourceService.getPortalSetting("Chat_ChatCode")?.Value,
          ChatClientName: this.resourceService.getPortalSetting("Chat_ChatClientName")?.Value,
          RecordTypeId: this.resourceService.getPortalSetting("Chat_RecordTypeId")?.Value,
          ChatUrl: this.resourceService.getPortalSetting("Chat_ChatUrl")?.Value,
          BaseLiveAgentContentURL: this.resourceService.getPortalSetting("Chat_BaseLiveAgentContentURL")?.Value,
          DeploymentId: this.resourceService.getPortalSetting("Chat_DeploymentId")?.Value,
        }
        if (!window.embedded_svc) {
          const s = document.createElement('script');
          s.setAttribute('src', this.chatConfig.JsFile);
          s.onload = () => {
            this.initChat(null, userClaims);
          };
        } else {
          this.initChat(this.chatConfig.ServiceUrl, userClaims);
        }

        let isChatEnabled = this.loggedInUserSettings.isFeatureEnabled(selectedContract.dealerId, CHATGROUP);
        if (!this.loggedInUserSettings.isFeatureEnabled(selectedContract.dealerId, CHAT)) {
          isChatEnabled = false;
        }
        if (!isChatEnabled) {
          setTimeout(() => {
            this.setChatButtonDisplay('none');
          }, 500);
        }
      }
    });
  }

  initChat(gslbBaseURL: string | null, userClaims: UserClaims) {
    embedded_svc.settings.displayHelpButton = true;
    embedded_svc.settings.language = '';

    const firstName = userClaims.given_name;
    const lastName = userClaims.family_name;
    const phone = this.phoneNumber;
    const email = userClaims.email;
    const clientName = this.chatConfig.ClientName ? this.chatConfig.ClientName : null;
    const customerLanguage = this.chatConfig.CustomerLanguage ? this.chatConfig.CustomerLanguage : null;
    const personRecordTypeId = this.chatConfig.PersonRecordTypeId ? this.chatConfig.PersonRecordTypeId : null;
    const recordTypeId = this.chatConfig.RecordTypeId ? this.chatConfig.RecordTypeId : null;

    embedded_svc.settings.extraPrechatFormDetails = [
      {
        label: 'CustomerName',
        value: firstName + lastName,
        transcriptFields: ['Customer_Name__c'],
        displayToAgent: true
      }, {
        label: 'ClientName',
        value: clientName,
        transcriptFields: ['Line_Of_Business__c'],
        displayToAgent: true
      }, {
        label: 'CustomerLanguage',
        value: customerLanguage,
        transcriptFields: ['Customer_Language__c'],
        displayToAgent: true
      }, {
        label: 'First Name',
        value: firstName,
        displayToAgent: true
      }, {
        label: 'Last Name',
        value: lastName,
        displayToAgent: true
      }, {
        label: 'Email',
        value: email,
        transcriptFields: ['Customer_Email__c'],
        displayToAgent: true
      }, {
        label: 'Phone',
        value: phone,
        transcriptFields: ['Customer_Phone__c'],
        displayToAgent: true
      }, {
        label: 'personRecordtypeid',
        value: personRecordTypeId
      }, {
        label: 'recordtypeid',
        value: recordTypeId
      }, {
        label: 'lineOfBusiness',
        value: clientName
      }, {
        label: 'Issue',
        value: 'Case from Chat',
        displayToAgent: true
      }, {
        label: 'Origin',
        value: 'Chat',
        displayToAgent: true
      }, {
        label: 'Status',
        value: 'Open',
        displayToAgent: true
      }];
    embedded_svc.settings.extraPrechatInfo = [
      {
        entityFieldMaps: [{
          doCreate: false,
          doFind: false,
          fieldName: 'LastName',
          isExactMatch: false,
          label: 'Last Name'
        }, {
          doCreate: false,
          doFind: false,
          fieldName: 'FirstName',
          isExactMatch: false,
          label: 'First Name'
        }, {
          doCreate: false,
          doFind: false,
          fieldName: 'Email',
          isExactMatch: false,
          label: 'Email'
        }, {
          doCreate: false,
          doFind: false,
          fieldName: 'Phone',
          isExactMatch: false,
          label: 'Phone'
        }],
        entityName: 'Contact',
        linkToEntityName: '',
        saveToTranscript: '',
        showOnCreate: false

      },
      {
        entityName: 'Account',
        showOnCreate: true,
        linkToEntityName: 'Case',
        linkToEntityField: 'AccountId',
        saveToTranscript: 'AccountId',
        entityFieldMaps: [{
          isExactMatch: false,
          fieldName: 'FirstName',
          doCreate: true,
          doFind: false,
          label: 'First Name'
        }, {
          isExactMatch: false,
          fieldName: 'LastName',
          doCreate: true,
          doFind: false,
          label: 'Last Name'
        }, {
          isExactMatch: false,
          fieldName: 'PersonEmail',
          doCreate: true,
          doFind: false,
          label: 'Email'
        }, {
          isExactMatch: true,
          fieldName: 'Phone',
          doCreate: true,
          doFind: true,
          label: 'Phone'
        }, {
          doCreate: true,
          doFind: false,
          fieldName: 'RecordTypeId',
          isExactMatch: false,
          label: 'personRecordtypeid'
        }, {
          doCreate: false,
          doFind: true,
          fieldName: 'RecordTypeId',
          isExactMatch: true,
          label: 'recordtypeid'
        },
        {
          doCreate: true,
          doFind: false,
          fieldName: 'Chat_Record_Id__c',
          isExactMatch: false,
          label: 'recordtypeid'
        },
        {
          doCreate: true,
          doFind: false,
          fieldName: 'Chat_Line_Of_Business__c',
          isExactMatch: false,
          label: 'lineOfBusiness'
        }]
      }, {
        entityName: 'Case',
        showOnCreate: true,
        saveToTranscript: 'CaseId',
        entityFieldMaps: [{
          isExactMatch: false,
          fieldName: 'Subject',
          doCreate: true,
          doFind: false,
          label: 'Issue'
        }, {
          isExactMatch: false,
          fieldName: 'Origin',
          doCreate: true,
          doFind: false,
          label: 'Origin'
        }, {
          isExactMatch: false,
          fieldName: 'SuppliedEmail',
          doCreate: true,
          doFind: false,
          label: 'Email'
        }, {
          isExactMatch: false,
          fieldName: 'Status',
          doCreate: true,
          doFind: false,
          label: 'Status'
        }]
      }];

    embedded_svc.settings.defaultMinimizedText = '';
    embedded_svc.settings.disabledMinimizedText = '';
    embedded_svc.settings.offlineSupportMinimizedText = '';
    embedded_svc.settings.loadingText = 'Loading...';
    embedded_svc.settings.enabledFeatures = ['LiveAgent'];
    embedded_svc.settings.entryFeature = 'LiveAgent';

    embedded_svc.init(
      this.chatConfig.ChatUrl,
      this.chatConfig.PtpUrl,
      gslbBaseURL,
      this.chatConfig.ChatCode,
      this.chatConfig.ChatClientName,
      {
        baseLiveAgentContentURL: this.chatConfig.BaseLiveAgentContentURL,
        deploymentId: this.chatConfig.DeploymentId,
        buttonId: this.chatConfig.ButtonId,
        baseLiveAgentURL: this.chatConfig.BaseLiveAgentURL,
        eswLiveAgentDevName: this.chatConfig.EswLiveAgentDevName,
        isOfflineSupportEnabled: false
      });
  }

  setChatButtonDisplay(display: string) {
    const elem: HTMLDivElement = document.querySelector('.embeddedServiceHelpButton');
    if (elem) elem.style.display = display;
  }

  hideChatButton() {
    // Set up a mutation observer to watch for the chat button being added to the DOM
    const observer = new MutationObserver((mutations) => {
      const chatButton = document.querySelector('.embeddedServiceHelpButton');
      if (chatButton && !chatButton.classList.contains('embeddedServiceHelpButton_hidden')) {
        const elem = document.querySelector('.embeddedServiceHelpButton');
        if (elem) elem.classList.add('embeddedServiceHelpButton_hidden');
        observer.disconnect();
      }
    });

    // Start observing the document body for DOM changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}
